import { CartItem } from '../types';
import { OrderApiResponse } from './types';
import { getStoreInfo } from './api';
import { formatCustomerPhone } from './customerHelpers';
import { applyLoyaltyDiscount } from './loyaltyRedeemApi';
import { applyHappyHour } from './happyHourApi';

export async function createTakeawayOrder(
  items: CartItem[],
  customerName: string,
  total: number,
  itemNotes: { [key: string]: string },
  cartResult?: any,
  invoiceResult?: any,
  paymentMode: string = 'cash',
  customerPhone: string = '',  // v5 fidélité: numéro optionnel pour cumuler les points
  loyaltyDiscount: number = 0, // v5 fidélité redeem
  loyaltyPointsUsed: number = 0,
  happyHourEligibleAmount: number = 0 // v5 happy hour: somme des prix NORMAUX éligibles
): Promise<OrderApiResponse> {
  try {
    const storeInfo = getStoreInfo();
    if (!storeInfo) {
      throw new Error('Store information not available. Please login first.');
    }

    // Log pour vérifier la configuration
    console.log('storeInfo.is_tax_included:', storeInfo.is_tax_included);
    console.log('storeInfo.item_tax:', storeInfo.item_tax);

    // Split address into parts for city
    const addressParts = storeInfo.address.split(',');
    const city = addressParts.length > 1 ? addressParts[1].trim() : '';

    // v5 fidélité: téléphone client formaté (vide si non saisi)
    const formattedPhone = formatCustomerPhone(customerPhone);
    if (formattedPhone) {
      console.log('[Loyalty] Téléphone client envoyé pour fidélité:', formattedPhone);
    }

    // Calcul des taxes
    const taxRate = storeInfo.item_tax; // Taux brut (ex. 20 pour 20%)
    const taxRateDecimal = taxRate / 100; // Taux décimal (ex. 0.2)
    let subtotalHT: number;
    let taxAmount: number;
    let displayTotal: number;

    if (storeInfo.is_tax_included) {
      // Si la taxe est incluse dans le total
      subtotalHT = total / (1 + taxRateDecimal); // Extrait le HT
      taxAmount = total - subtotalHT; // Taxe incluse dans le total
      displayTotal = total; // Total TTC = total fourni
    } else {
      // Si la taxe n’est pas incluse
      subtotalHT = total; // Total fourni est HT
      taxAmount = subtotalHT * taxRateDecimal; // Calcule la taxe
      displayTotal = subtotalHT + taxAmount; // Total TTC
    }

    console.log('Calculs - subtotalHT:', subtotalHT, 'taxAmount:', taxAmount, 'displayTotal:', displayTotal);

    // 1. Add to cart (si non fourni)
    let finalCartResult = cartResult;
    if (!cartResult) {
      const cartData = {
        cart_id: "",
        cart_unique_token: storeInfo.server_token,
        delivery_types: "",
        delivery_type: 1,
        destination_addresses: [{
          address: storeInfo.address,
          address_type: "destination",
          city: city,
          delivery_status: 0,
          location: storeInfo.location,
          note: "",
          user_details: {
            country_phone_code: storeInfo.country_phone_code,
            email: "",
            first_name: "",
            image_url: "",
            last_name: "",
            name: customerName ? `Borne ${customerName}` : "Guest",
            phone: formattedPhone, // v5 fidélité: téléphone du client (vide si non saisi)
            user_type: 7
          }
        }],
        id: storeInfo.store_id,
        is_guest_order: true,
        is_tax_included: storeInfo.is_tax_included,
        is_use_item_tax: false,
        order_details: [{
          items: items.map(item => ({
            item_id: item.menuItem.id,
            unique_id: Math.floor(Math.random() * 1000),
            item_name: item.menuItem.name,
            quantity: item.quantity,
            details: item.menuItem.description,
            image_url: [item.menuItem.image],
            item_price: item.menuItem.price,
            note_for_item: itemNotes[item.uniqueId] || "",
            specifications: Object.entries(item.customizations).map(([specName, options]) => ({
              list: options.map(opt => ({
                _id: `${item.menuItem.id}-${specName}-${opt.id}`,
                main_specification_id: null,
                franchise_id: null,
                name: opt.name,
                price: opt.price,
                quantity: 1,
                is_default_selected: false,
                is_user_selected: true
              })),
              name: specName,
              price: 0,
              type: options.length > 1 ? 2 : 1
            })),
            tax: storeInfo.item_tax,
            tax_details: [],
            total_item_price: item.menuItem.price * item.quantity,
            total_item_tax: storeInfo.is_tax_included ? 0 : (item.menuItem.price * item.quantity) * taxRateDecimal,
            total_specification_tax: 0,
            product_id: item.menuItem.id,
            product_name: item.menuItem.name
          })),
          product_id: items[0]?.menuItem.id,
          product_name: items[0]?.menuItem.name,
          total_item_price: subtotalHT,
          total_item_tax: taxAmount,
          unique_id: Math.floor(Math.random() * 1000)
        }],
        pickup_addresses: [{
          address: storeInfo.address,
          address_type: "pickup",
          delivery_status: 0,
          location: storeInfo.location,
          note: "",
          user_details: {
            name: storeInfo.name,
            email: storeInfo.email,
            phone: storeInfo.phone,
            country_phone_code: storeInfo.country_phone_code,
            image_url: storeInfo.image_url,
            user_type: 2
          }
        }],
        store_id: storeInfo.store_id,
        table_no: "0",
        timezone: "Europe/Paris",
        total_cart_price: subtotalHT,
        total_item_tax: taxAmount,
        user_id: "",
        user_type: 2
      };

      console.log('Sending cart data:', JSON.stringify(cartData, null, 2));

      const cartResponse = await fetch('https://api.restomi.fr/v3/api/user/add_item_in_cart', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'server-token': storeInfo.server_token
        },
        body: JSON.stringify(cartData)
      });

      if (!cartResponse.ok) {
        const errorText = await cartResponse.text();
        throw new Error(`Cart error: ${errorText}`);
      }

      finalCartResult = await cartResponse.json();
      console.log('Cart response:', finalCartResult);

      if (!finalCartResult.success) {
        throw new Error(finalCartResult.message || "Erreur lors de l'ajout au panier");
      }
    }

    // 2. Generate invoice (si non fourni)
    let finalInvoiceResult = invoiceResult;

    // v5 BUG FIX CRITIQUE (NF525): même garde-fou que dineIn
    if (finalInvoiceResult) {
      const invoiceCartId = finalInvoiceResult.order_payment?.cart_id || finalInvoiceResult.cart_id;
      if (invoiceCartId && invoiceCartId !== finalCartResult.cart_id) {
        console.warn('[NF525 GUARD] invoiceResult appartient à un autre cart, on l\'ignore', {
          invoiceCartId,
          currentCartId: finalCartResult.cart_id,
          oldOrderPaymentId: finalInvoiceResult.order_payment?._id,
        });
        finalInvoiceResult = null;
      }
    }

    if (!finalInvoiceResult) {
      const invoiceData = {
        cart_id: finalCartResult.cart_id,
        delivery_types: "",
        id: storeInfo.store_id,
        is_guest_order: true,
        is_store_place_order: false,
        is_tax_included: storeInfo.is_tax_included,
        is_use_item_tax: false,
        is_user_pick_up_order: true,
        order_type: 2,
        store_id: storeInfo.store_id,
        timezone: "Europe/Paris",
        total_cart_amout_without_tax: subtotalHT,
        total_cart_price: subtotalHT,
        total_distance: 0,
        total_item_count: items.length,
        total_item_tax: taxAmount,
        total_time: 0,
        user_id: finalCartResult.user_id || ""
      };

      console.log('Sending invoice data:', JSON.stringify(invoiceData, null, 2));

      const invoiceResponse = await fetch('https://api.restomi.fr/v3/api/user/get_order_cart_invoice', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'server-token': storeInfo.server_token
        },
        body: JSON.stringify(invoiceData)
      });

      if (!invoiceResponse.ok) {
        const errorText = await invoiceResponse.text();
        throw new Error(`Invoice error: ${errorText}`);
      }

      finalInvoiceResult = await invoiceResponse.json();
      console.log('Invoice response:', finalInvoiceResult);

      if (!finalInvoiceResult.success) {
        throw new Error(finalInvoiceResult.message || "Erreur lors de la génération de la facture");
      }
    }

    // Ajuster displayTotal avec les données de l'invoice si disponibles
    if (finalInvoiceResult && !storeInfo.is_tax_included) {
      displayTotal = finalInvoiceResult.order_payment.total;
      taxAmount = finalInvoiceResult.order_payment.total_store_tax_price || taxAmount;
      subtotalHT = finalInvoiceResult.order_payment.total_cart_price || subtotalHT;
      console.log('Ajustements après invoice - subtotalHT:', subtotalHT, 'taxAmount:', taxAmount, 'displayTotal:', displayTotal);
    }

    // v5 HAPPY HOUR (étape 2.4) : déclarer la remise AVANT apply_loyalty_discount
    // et AVANT pay_order_payment.
    //
    // ⚠️ LE SERVEUR FAIT AUTORITÉ : il revérifie que le happy hour est actif
    // (jour, heure, canal) et recalcule la remise avec SON pourcentage.
    let appliedHappyHourDiscount = 0;
    let appliedHappyHourName = '';
    let appliedHappyHourPercent = 0;
    let happyHourExpired = false;
    if (happyHourEligibleAmount > 0) {
      console.log('[HappyHour] Déclaration de la remise AVANT paiement (takeaway):', {
        order_payment_id: finalInvoiceResult.order_payment._id,
        eligible_amount: happyHourEligibleAmount,
      });
      const hhResult = await applyHappyHour(
        finalInvoiceResult.order_payment._id,
        happyHourEligibleAmount
      );
      if (hhResult.success && hhResult.applied && hhResult.happy_hour_discount > 0) {
        appliedHappyHourDiscount = hhResult.happy_hour_discount;
        appliedHappyHourName = hhResult.happy_hour_name || '';
        appliedHappyHourPercent = hhResult.happy_hour_percent || 0;
        displayTotal = Math.max(0, displayTotal - appliedHappyHourDiscount);
        console.log('[HappyHour] Remise officielle appliquée:', {
          name: appliedHappyHourName,
          percent: appliedHappyHourPercent,
          discount: appliedHappyHourDiscount,
          new_display_total: displayTotal,
        });
      } else {
        console.warn('[HappyHour] Remise refusée par le serveur (happy hour terminé ?)', hhResult);
        happyHourExpired = true;
      }
    }

    // v5 fidélité REDEEM (étape 2.5) : appliquer la réduction sur l'order_payment
    // AVANT pay_order_payment.
    //
    // v5 NOUVEAU COMPORTEMENT : apply_loyalty_discount déduit AUSSI les points
    // atomiquement (pour bloquer la double-utilisation sur commandes simultanées).
    // Donc plus besoin d'appeler redeem_loyalty_points ensuite.
    //
    // Gestion d'échec :
    //  - reason="points_already_used" → un autre kiosk a déjà utilisé les points
    //  - autre échec → message générique "voir avec la caisse"
    let loyaltyApplyFailed = false;
    let loyaltyFailReason = '';
    let appliedLoyaltyDiscount = 0;
    let appliedLoyaltyPoints = 0;
    if (loyaltyDiscount > 0) {
      const formattedPhone = formatCustomerPhone(customerPhone);
      const note = `Réduction fidélité -${loyaltyPointsUsed} pts = -${loyaltyDiscount.toFixed(2)}€`;
      console.log('[LoyaltyRedeem] Application de la réduction sur order_payment AVANT paiement (takeaway):', {
        order_payment_id: finalInvoiceResult.order_payment._id,
        original_total: displayTotal,
        discount: loyaltyDiscount,
        points_used: loyaltyPointsUsed,
        phone: formattedPhone,
        expected_new_total: Math.max(0, displayTotal - loyaltyDiscount),
      });
      const applyResult = await applyLoyaltyDiscount(
        finalInvoiceResult.order_payment._id,
        loyaltyDiscount,
        loyaltyPointsUsed,
        formattedPhone,
        note
      );
      if (!applyResult.success) {
        console.warn('[LoyaltyRedeem] Échec apply_loyalty_discount → on continue SANS réduction', {
          reason: applyResult.reason,
          message: applyResult.message,
        });
        loyaltyApplyFailed = true;
        loyaltyFailReason = applyResult.reason || '';
        // displayTotal reste au montant complet
      } else {
        if (typeof applyResult.new_total === 'number') {
          displayTotal = applyResult.new_total;
          console.log('[LoyaltyRedeem] Nouveau total order_payment confirmé par backend:', displayTotal);
        } else {
          displayTotal = Math.max(0, displayTotal - loyaltyDiscount);
        }
        appliedLoyaltyDiscount = loyaltyDiscount;
        appliedLoyaltyPoints = loyaltyPointsUsed;
        console.log('[LoyaltyRedeem] Points déduits atomiquement par apply_loyalty_discount:', {
          points_used: applyResult.points_used,
          new_balance: applyResult.new_balance,
        });
      }
    }

    // 3. Process payment
    // v5 NF525: card_machine reste en card_machine (TPE physique gère),
    // tous les autres modes (cash, ticket_resto, etc.) deviennent "pending"
    // pour que le caissier finalise l'encaissement au comptoir avec le vrai mode.
    // is_payment_mode_cash reste toujours true depuis le kiosk (flag "pas Stripe").
    const finalPaymentMode = paymentMode === 'card_machine' ? 'card_machine' : 'pending';

    // v5 fidélité REDEEM: le backend ignore "total" dans pay_order_payment, c'est
    // order_payment.total qui fait foi. apply_loyalty_discount ci-dessus l'a déjà mis
    // à jour côté backend. Donc paymentData reste minimal.
    const paymentData: any = {
      cart_id: finalCartResult.cart_id,
      id: storeInfo.store_id,
      is_guest_order: true,
      is_payment_mode_cash: true,
      payment_mode: finalPaymentMode,
      order_source: 'kiosk',
      order_payment_id: finalInvoiceResult.order_payment._id,
      order_type: 2,
      store_id: storeInfo.store_id,
      timezone: "Europe/Paris",
      user_id: finalCartResult.user_id || ""
    };

    console.log('[NF525] paymentMode UI:', paymentMode, '→ envoyé API:', finalPaymentMode);
    console.log('Sending payment data:', JSON.stringify(paymentData, null, 2));

    const paymentResponse = await fetch('https://api.restomi.fr/v3/api/user/pay_order_payment', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'server-token': storeInfo.server_token
      },
      body: JSON.stringify(paymentData)
    });

    if (!paymentResponse.ok) {
      const errorText = await paymentResponse.text();
      throw new Error(`Payment error: ${errorText}`);
    }

    const paymentResult = await paymentResponse.json();
    console.log('Payment response:', paymentResult);

    if (!paymentResult.success) {
      throw new Error(paymentResult.message || "Erreur lors du paiement");
    }

    // 4. Create final order
    // Note: on n'écrit la note QUE si la réduction a vraiment été appliquée
    // (pas si apply_loyalty_discount a échoué)
    const loyaltyNote = appliedLoyaltyDiscount > 0
      ? `Réduction fidélité -${appliedLoyaltyPoints} pts = -${appliedLoyaltyDiscount.toFixed(2)}€`
      : '';
    const createOrderData = {
      cart_id: finalCartResult.cart_id,
      delivery_note: loyaltyNote,
      id: storeInfo.store_id,
      is_user_pick_up_order: true,
      is_store_place_order: false,
      is_guest_order: true,
      delivery_type: 1,
      order_source: 'kiosk',
      order_payment_id: finalInvoiceResult.order_payment._id,
      order_type: 2,
      store_id: storeInfo.store_id,
      timezone: "Europe/Paris",
      user_id: finalCartResult.user_id || ""
    };

    // v5 NF525 LOG: traçabilité explicite de l'order_payment_id utilisé.
    // Doit être DIFFÉRENT à chaque commande. Si identique → bug réutilisation.
    console.log('[NF525] order_payment_id utilisé pour create_order (takeaway):', finalInvoiceResult.order_payment._id, {
      cart_id: finalCartResult.cart_id,
      timestamp: new Date().toISOString(),
    });
    console.log('Sending create order data:', JSON.stringify(createOrderData, null, 2));

    const createOrderResponse = await fetch('https://api.restomi.fr/v3/api/user/create_order', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'server-token': storeInfo.server_token
      },
      body: JSON.stringify(createOrderData)
    });

    if (!createOrderResponse.ok) {
      const errorText = await createOrderResponse.text();
      throw new Error(`Create order error: ${errorText}`);
    }

    const createOrderResult = await createOrderResponse.json();
    console.log('Create order response:', createOrderResult);

    if (!createOrderResult.success) {
      // v5 Mode Rush: le backend refuse les commandes pendant un rush.
      // On remonte le refus proprement (sans throw) pour que l'UI affiche
      // l'écran de blocage avec le message + le compteur.
      // v5 Modules: le module kiosk a été désactivé par l'admin Restomi.
      // Distinct du rush : ici c'est un blocage administratif, pas une pause resto.
      if (createOrderResult.reason === 'module_disabled') {
        console.warn('[Modules] create_order refusé: module_disabled', createOrderResult);
        return {
          success: false,
          message: createOrderResult.error_description || "Module Kiosk desactive pour ce restaurant. Contactez Restomi.",
          module_disabled: true,
          module_name: createOrderResult.module || 'kiosk',
          module_error_description: createOrderResult.error_description || '',
          displayTotal,
          subtotal: subtotalHT,
          taxAmount,
          taxRate,
        };
      }
      if (createOrderResult.reason === 'rush_active') {
        console.warn('[Rush] create_order refusé: rush_active', createOrderResult);
        return {
          success: false,
          message: createOrderResult.error_description || "Commandes momentanément suspendues",
          rush_active: true,
          rush_remaining_seconds: Math.max(0, Number(createOrderResult.remaining_seconds) || 0),
          rush_message: createOrderResult.error_description || '',
          displayTotal,
          subtotal: subtotalHT,
          taxAmount,
          taxRate,
        };
      }
      throw new Error(createOrderResult.message || "Erreur lors de la création de la commande");
    }

    // Retourne la réponse avec order_id ET unique_id (pour redeem_loyalty_points).
    // displayTotal est déjà à jour (réduit par apply_loyalty_discount si applicable).
    return {
      success: true,
      order_id: createOrderResult.order_id,
      unique_id: createOrderResult.unique_id || createOrderResult.order?.unique_id, // v5 fidélité redeem
      cart_id: finalCartResult.cart_id,
      user_id: finalCartResult.user_id,
      displayTotal,
      subtotal: subtotalHT,
      taxAmount,
      taxRate,
      // v5 fidélité: flag pour que l'UI sache si la réduction a réussi ou pas
      loyalty_apply_failed: loyaltyApplyFailed,
      loyalty_fail_reason: loyaltyFailReason,
      loyalty_applied_discount: appliedLoyaltyDiscount,
      loyalty_applied_points: appliedLoyaltyPoints,
      // v5 happy hour: montants OFFICIELS renvoyés par le serveur
      happy_hour_discount: appliedHappyHourDiscount,
      happy_hour_name: appliedHappyHourName,
      happy_hour_percent: appliedHappyHourPercent,
      happy_hour_expired: happyHourExpired,
    };
  } catch (error) {
    console.error('Error creating takeaway order:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : "Une erreur est survenue",
      displayTotal: total,
      subtotal: total,
      taxAmount: 0,
      taxRate: 0
    };
  }
}